import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  AccessControl,
  ExpandedBlock,
  MuiButton,
  RideAddPassenger,
  RideAddStation,
  RideStationsPanel,
  RideStationsPanelGreyed,
} from "@ryde/controls";
import { useRideEmptyStationsAllowed } from "@ryde/redux";
import {
  editFormDataAF,
  IRideDto,
  isPastTaxiSupplierRide,
  isRideGreyed,
  RideCrudModeKeyTuple,
  RideStationPanelMode,
} from "@ryde/shared";

import { RideTabsStationsPanelStyled } from "./ride-tabs-stations-panel.styled";

type ExpendType = "addPassenger" | "addStation";

interface ITabStationExpend {
  addPassenger: boolean;
  addStation: boolean;
}

export const RideTabsStationsPanel: FC = () => {
  const { t } = useTranslation();
  const rideDto = useRecoilValue<IRideDto | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const isGreyed: boolean = isRideGreyed(rideDto);
  // RYDE-7633: a past taxi-supplier ride is read-only — render the greyed (non-interactive) stations
  // view and hide the add-station / add-passenger controls.
  const isReadOnlyPastTaxi: boolean = isPastTaxiSupplierRide(rideDto);
  const isStationsLocked = isGreyed || isReadOnlyPastTaxi;
  const [isExpanded, setExpanded] = useState<ITabStationExpend>({
    addPassenger: false,
    addStation: false,
  });

  const {
    isRideWithEmptyStationsAllowed,
    isRideWithDuplicatedStationsAllowed,
  } = useRideEmptyStationsAllowed(RideStationPanelMode.CRUD_TAB, rideDto);

  const openExpanded = useCallback(
    (expendType: ExpendType) =>
      setExpanded(prev => ({ ...prev, [expendType]: true })),
    [setExpanded],
  );
  const closeExpanded = useCallback(
    (expendType: ExpendType) =>
      setExpanded(prev => ({ ...prev, [expendType]: false })),
    [setExpanded],
  );

  return (
    <RideTabsStationsPanelStyled
      className={clsx({
        "ride-tabs-station-panel_expanded":
          isExpanded.addPassenger || isExpanded.addStation,
      })}
    >
      {rideDto && !isStationsLocked && (
        <RideStationsPanel
          rideDto={rideDto}
          mode={RideStationPanelMode.CRUD_TAB}
        />
      )}
      {rideDto && isStationsLocked && (
        <RideStationsPanelGreyed
          rideDto={rideDto}
          mode={RideStationPanelMode.CRUD_TAB}
        />
      )}

      {!isExpanded.addStation &&
        isRideWithEmptyStationsAllowed &&
        !isReadOnlyPastTaxi && (
          <MuiButton
            muiVariant="text"
            onClick={() => openExpanded("addStation")}
          >
            {t(`general.ADD_A_NEW_ENTITY`, { entity: t(`station.single`) })} +
          </MuiButton>
        )}

      {!isExpanded.addPassenger &&
        !isRideWithEmptyStationsAllowed &&
        !isReadOnlyPastTaxi && (
          <MuiButton
            muiVariant="text"
            onClick={() => openExpanded("addPassenger")}
          >
            {t(`general.ADD_A_NEW_ENTITY`, { entity: t(`passenger.single`) })} +
          </MuiButton>
        )}

      {rideDto && !isReadOnlyPastTaxi && (
        <AnimatePresence initial={false}>
          {isExpanded.addStation && isRideWithEmptyStationsAllowed && (
            <ExpandedBlock key="addStation">
              <RideAddStation
                rideDto={rideDto}
                closeExpanded={() => closeExpanded("addStation")}
                isRideWithDuplicatedStationsAllowed={
                  isRideWithDuplicatedStationsAllowed
                }
              />
            </ExpandedBlock>
          )}

          {isExpanded.addPassenger && !isRideWithEmptyStationsAllowed && (
            <ExpandedBlock key="addPassenger">
              <AccessControl entity="ride" action="update">
                <RideAddPassenger
                  rideDto={rideDto}
                  closeExpanded={() => closeExpanded("addPassenger")}
                />
              </AccessControl>
            </ExpandedBlock>
          )}
        </AnimatePresence>
      )}
    </RideTabsStationsPanelStyled>
  );
};
